import React, { useEffect, Suspense } from 'react';
import PropTypes from 'prop-types';
import './_sidebar.scss';
import styled, { useTheme } from 'styled-components';
import { NavLink } from 'react-router-dom';
import { SideMenuAccess } from '../../../midleware/midleware';
import { sideMenuState } from '../../../features/auth/authSlice';
import { occyLs } from '../../../utils/localStorage';
import { useDispatch } from 'react-redux';
import AnalyticsIcon from '../../components/svgIcons/AnalyticsIcon';
import HelpIcon from '../../components/svgIcons/QuestionMark';
import HiringRequestIcon from '../../components/svgIcons/HiringRequestIcon';
import { setEventType } from '../../../features/Setting/AccountCalender/AccountSlice';
import { useGetAvailabilityQuery } from '../../../features/Setting/AccountCalender/CalenderApiSlice';
import { USERROLES } from '../../../features/users/creation/assets/utills';
import DashboardIcon from '../../components/svgIcons/DashboardIcon';
import JobsIcon from '../../components/svgIcons/JobsIcon';
import CandidatesIcon from '../../components/svgIcons/CandidatesIcon';
import SettingsIcon from '../../components/svgIcons/SettingsIcon';
import OpenSidebarIcon from '../../components/svgIcons/OpenSidebarIcon';
import CalendarFillIcon from '../../components/svgIcons/CalenderFillIcon';
import SideBarIcon from '../../components/svgIcons/SidebarIcon';
import OfferAgreementDashboardIcon from '../../components/svgIcons/OfferAgreementDashboardIcon';
import OccyLogoWhite from '../../components/svgIcons/OccyLogoWhite';
import SidebarCredits from './SidebarCredits';
import SidebarWaves from '../../components/svgIcons/SidebarWaves.png';
import SidebarBubble from '../../components/svgIcons/SidebarBubble.png';
import StyledToolTip from '../../components/styledComponents/StyledToolTip';
import {
  PERMISSIONS,
  usePermissions,
  usePermissionActionGuard,
} from '../../../permission-system';
import { canAccessMyRequests } from '../../../features/AuthorityToRecruit/viewConfig';

const NAV_SECTIONS = [
  { key: 'recruiting', label: 'Recruiting' },
  { key: 'manage', label: 'Manage' },
];

const MenuIcon = ({ children }) => (
  <Suspense fallback={<div>Loading...</div>}>{children}</Suspense>
);

MenuIcon.propTypes = {
  children: PropTypes.node.isRequired,
};

const SideBar = ({ openSideMenu, setOpenSideMenu }) => {
  const { hasWildcard, permissionKeys, enforcing } = usePermissions();
  const hasNavPermission = (permission) => {
    if (!permission) return true;
    if (!enforcing) return false;
    if (hasWildcard) return true;
    return permissionKeys.includes(permission);
  };
  const { data: availability } = useGetAvailabilityQuery();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { can } = usePermissionActionGuard();
  const canAccessMyRequestsRoute = canAccessMyRequests(can);

  useEffect(() => {
    if (availability) {
      dispatch(
        setEventType(availability?.autoAvailability === false ? 2 : 1),
      );
    }
  }, [availability, dispatch]);

  const sidebarVisible = !!occyLs.getObject('APP_SIDEBAR');
  const MenuItems = [
    {
      name: 'Dashboard',
      icon: (
        <MenuIcon>
          <DashboardIcon />
        </MenuIcon>
      ),
      to: 'dashboard',
      authorizedRoles: [
        USERROLES.ADMIN,
        USERROLES.RECRUITER,
        USERROLES.INTERVIEWER,
        USERROLES.HIRINGMANAGER,
        USERROLES.BILLINGADMIN,
        USERROLES.APPROVER,
      ],
      childUser: true,
    },
    {
      name: 'Jobs',
      icon: (
        <MenuIcon>
          <JobsIcon />
        </MenuIcon>
      ),
      to: 'jobs',
      authorizedRoles: [
        USERROLES.ADMIN,
        USERROLES.RECRUITER,
        USERROLES.HIRINGMANAGER,
        USERROLES.INTERVIEWER,
      ],
      childUser: false,
    },
    {
      name: 'Hiring Requests',
      icon: (
        <MenuIcon>
          <HiringRequestIcon />
        </MenuIcon>
      ),
      to: 'authority-to-recruit/inbox',
      authorizedRoles: [
        USERROLES.ADMIN,
        USERROLES.RECRUITER,
        USERROLES.HIRINGMANAGER,
        USERROLES.INTERVIEWER,
      ],
      childUser: false,
    },
    {
      name: 'My Requests',
      icon: (
        <MenuIcon>
          <HiringRequestIcon />
        </MenuIcon>
      ),
      to: 'authority-to-recruit/my-requests',
      authorizedRoles: [
        USERROLES.ADMIN,
        USERROLES.RECRUITER,
        USERROLES.HIRINGMANAGER,
        USERROLES.INTERVIEWER,
      ],
      childUser: false,
      hidden: !canAccessMyRequestsRoute,
    },
    {
      name: 'Offers & Agreements',
      icon: (
        <MenuIcon>
          <OfferAgreementDashboardIcon />
        </MenuIcon>
      ),
      to: 'offers-and-agreements',
      authorizedRoles: [USERROLES.ADMIN, USERROLES.RECRUITER],
      requiredPermission: PERMISSIONS.MANAGE_DOCUMENTS,
      nobasicAllowed: true,
    },
    {
      name: 'Candidates',
      icon: (
        <MenuIcon>
          <CandidatesIcon sideColor={theme?.secondary} size={23} />
        </MenuIcon>
      ),
      to: 'candidates',
      authorizedRoles: [
        USERROLES.ADMIN,
        USERROLES.RECRUITER,
        USERROLES.HIRINGMANAGER,
      ],
      nobasicAllowed: true,
    },
    {
      name: 'Calendar',
      icon: (
        <MenuIcon>
          <CalendarFillIcon />
        </MenuIcon>
      ),
      to: 'calendar',
      authorizedRoles: [
        USERROLES.ADMIN,
        USERROLES.RECRUITER,
        USERROLES.INTERVIEWER,
        USERROLES.HIRINGMANAGER,
      ],
    },
    {
      name: 'Analytics',
      icon: (
        <MenuIcon>
          <AnalyticsIcon />
        </MenuIcon>
      ),
      to: 'analytics',
      authorizedRoles: [USERROLES.ADMIN, USERROLES.RECRUITER],
      requiredPermission: PERMISSIONS.MANAGE_REPORT_ANALYTICS,
      nobasicAllowed: true,
      section: 'manage',
    },
    {
      name: 'Settings',
      icon: (
        <MenuIcon>
          <SettingsIcon size="24px" />
        </MenuIcon>
      ),
      to: 'settings',
      childUser: true,
      section: 'manage',
    },
    {
      name: 'Help',
      icon: (
        <MenuIcon>
          <HelpIcon size="24px" />
        </MenuIcon>
      ),
      to: '',
      action: () => {
        openWidget();
      },
      section: 'manage',
    },
  ];

  const openWidget = () => {
    globalThis.FreshworksWidget('open');
  };

  const isItemVisible = (item) => {
    if (
      item?.requiredPermission &&
      !hasNavPermission(item.requiredPermission)
    ) {
      return false;
    }
    return SideMenuAccess(item) && !item.hidden;
  };

  const toggleSidebar = () => {
    dispatch(sideMenuState(!sidebarVisible));
    setOpenSideMenu(!openSideMenu);
  };

  return (
    <SideBarContainer
      $open={sidebarVisible}
      className={` ${
        sidebarVisible ? 'openSidebar' : 'closeSidebar'
      } vh-100  p-0 position-fixed d-flex flex-column `}
    >
      <div
        className={`${
          sidebarVisible
            ? 'd-flex justify-content-between align-items-center px-4 sidebar-brand-row'
            : 'd-flex justify-content-center align-items-center sidebar-brand-row sidebar-brand-row--collapsed'
        } `}
      >
        {sidebarVisible ? <OccyLogoWhite /> : null}
        <button
          type="button"
          className="menuButton cursor-pointer"
          aria-label={sidebarVisible ? 'Collapse sidebar' : 'Expand sidebar'}
          onClick={toggleSidebar}
        >
          {sidebarVisible ? <OpenSidebarIcon /> : <SideBarIcon />}
        </button>
      </div>
      <StyledSideMenu className={sidebarVisible ? 'is-open' : ''}>
        {NAV_SECTIONS.map((section, sectionIndex) => {
          const sectionItems = MenuItems.filter(
            (item) =>
              (item?.section || 'recruiting') === section.key &&
              isItemVisible(item),
          );
          if (sectionItems.length === 0) return null;
          return (
            <div className="nav-section" key={section.key}>
              {sidebarVisible ? (
                <p className="nav-section-label">{section.label}</p>
              ) : sectionIndex > 0 ? (
                <span className="section-divider" />
              ) : null}
              {sectionItems.map((item) => {
                const link = (
                  <NavLink
                    key={item.to || item.name}
                    target={item?.name === 'University' ? '_blank' : undefined}
                    to={item?.name === 'Help' ? undefined : item?.to}
                    className={({ isActive }) => {
                      const highlightable =
                        isActive &&
                        item?.name !== 'Help' &&
                        item?.name !== 'University';
                      const layoutClass = sidebarVisible
                        ? 'd-flex align-items-center px-3 ms-0'
                        : 'justify-content-center align-items-center d-flex';
                      return `hoverEffect ${
                        highlightable ? 'sidebar-nav-active text-white' : 'tab-link'
                      } ${layoutClass} text-decoration-none space `;
                    }}
                    onClick={(e) => {
                      if (
                        item?.requiredPermission &&
                        !hasNavPermission(item.requiredPermission)
                      ) {
                        e.preventDefault();
                        return;
                      }
                      item?.action?.(e);
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-center">
                      {item.icon}
                    </div>
                    {sidebarVisible ? (
                      <p className="ms-3 MenuText mb-0">{item.name}</p>
                    ) : null}
                  </NavLink>
                );
                return !sidebarVisible ? (
                  <StyledToolTip
                    key={item.name}
                    title={item.name}
                    placement="right"
                    childrenClass="ms-0"
                  >
                    {link}
                  </StyledToolTip>
                ) : (
                  <React.Fragment key={item.name}>{link}</React.Fragment>
                );
              })}
            </div>
          );
        })}
      </StyledSideMenu>

      {sidebarVisible ? (
        <SidebarDecor aria-hidden="true">
          <SidebarDecorStage>
            <SidebarWaveImg src={SidebarWaves} alt="" />
            <SidebarBubbleWrap>
              <SidebarBubbleImg src={SidebarBubble} alt="" />
            </SidebarBubbleWrap>
          </SidebarDecorStage>
        </SidebarDecor>
      ) : (
        <SidebarSpacer aria-hidden="true" />
      )}

      <SidebarCredits sidebarVisible={sidebarVisible} />
    </SideBarContainer>
  );
};

SideBar.propTypes = {
  openSideMenu: PropTypes.bool,
  setOpenSideMenu: PropTypes.func,
};

const StyledSideMenu = styled.div`
  &.is-open {
    padding-top: 20px;
  }

  .tab-link {
    color: rgba(255, 255, 255, 0.92);
  }
  .space {
    min-height: 52px;
    padding: 10px 0;
  }

  .nav-section {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .nav-section-label {
    margin: 0 24px;
    padding: 0;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.08em;
    line-height: 1.2;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.55);
  }

  .nav-section + .nav-section {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid rgba(255, 255, 255, 0.12);
  }

  .section-divider {
    display: block;
    height: 1px;
    margin: 0 14px 10px;
    background-color: rgba(255, 255, 255, 0.12);
  }

  .sidebar-nav-active {
    background: ${({ theme }) => theme.secondary || '#dc601e'} !important;
    box-shadow: ${({ theme }) => theme?.shadowSm || 'none'};
    color: #fff !important;
  }

  .sidebar-nav-active .MenuText {
    font-weight: ${({ theme }) => theme?.fontWeightSemibold || 600};
  }

  .sidebar-nav-active.hoverEffect:hover::before {
    width: 0 !important;
  }

  a {
    margin: 0;
  }

  a.space {
    border-radius: ${({ theme }) => theme?.radiusSm || '8px'};
    margin: 3px 12px !important;
    width: calc(100% - 24px);
  }

  .MenuText {
    font-size: 14px;
    font-weight: ${({ theme }) => theme?.fontWeightMedium || 500};
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  svg {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    color: #fff;
  }

  svg path,
  svg circle,
  svg rect,
  svg line,
  svg polyline,
  svg polygon {
    stroke: #fff !important;
  }

  svg path[fill]:not([fill='none']),
  svg circle[fill]:not([fill='none']) {
    fill: #fff !important;
  }

  flex: 0 1 auto;
  min-height: 0;
  overflow: auto;
  position: relative;
  @-moz-document url-prefix() {
    scrollbar-width: thin;
    scrollbar-color: ${({ theme }) => theme.primary} transparent;
  }
  ::-webkit-scrollbar-thumb,
  ::-webkit-scrollbar-track,
  ::-webkit-scrollbar-thumb:hover,
  ::-webkit-scrollbar {
    background: ${({ theme }) => theme.primary};
  }

  .hoverEffect {
    position: relative;
    color: white;
    overflow: hidden;
    z-index: 1;
    transition: color 0.3s ease;

    &:hover {
      color: white;
      text-shadow: 0 0 2px rgba(255, 255, 255, 0.8);
    }

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      width: 0;
      height: 100%;
      background-color: rgba(255, 255, 255, 0.08);
      transition: width 0.4s ease;
      z-index: -1;
    }

    &:hover::before {
      width: 100%;
    }
  }
`;

const SidebarDecor = styled.div`
  flex: 1 1 auto;
  min-height: 168px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px 16px;
  overflow: hidden;
  pointer-events: none;

  @media (max-height: 720px) {
    display: none;
  }
`;

const SidebarSpacer = styled.div`
  flex: 1 1 auto;
  min-height: 0;
`;

const SidebarDecorStage = styled.div`
  position: relative;
  width: 100%;
  height: 188px;
`;

const SidebarWaveImg = styled.img`
  position: absolute;
  right: 22px;
  left: auto;
  top: 50%;
  z-index: 1;
  width: 104%;
  max-width: none;
  height: auto;
  transform: translateY(-50%);
  mix-blend-mode: screen;
  filter: brightness(3.4) saturate(0.25);
  opacity: 0.42;
  user-select: none;
  pointer-events: none;
`;

const SidebarBubbleWrap = styled.div`
  position: absolute;
  left: 50%;
  top: 70%;
  z-index: 2;
  width: 79%;
  max-width: 203px;
  transform: translate(-50%, -48%);
  filter: drop-shadow(0 16px 20px rgba(0, 0, 0, 0.38));
  pointer-events: none;
`;

const SidebarBubbleImg = styled.img`
  display: block;
  width: 100%;
  height: auto;
  mix-blend-mode: screen;
  user-select: none;
  pointer-events: none;
`;

const SideBarContainer = styled.div`
  background: ${({ theme }) => theme?.sideBar} !important;
  width: ${({ $open }) => ($open ? '258px' : '70px')};
  max-width: ${({ $open }) => ($open ? '258px' : '70px')};
  color: #fff;
  overflow-x: hidden;

  .sidebar-brand-row {
    min-height: var(--app-header-height, 72px);
    box-sizing: border-box;
    flex-shrink: 0;
  }

  .sidebar-brand-row--collapsed {
    padding: 12px 0;
  }

  .menuButton {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    padding: 0;
    margin: 0;
    border: none;
    border-radius: 50%;
    background-color: ${({ theme }) => theme?.secondary || '#dc601e'};
    color: #fff;
    line-height: 0;
    transition: background-color 0.15s ease, transform 0.15s ease;

    &:hover {
      background-color: ${({ theme }) => theme?.secondaryDark || '#c4551a'};
    }

    &:focus-visible {
      outline: 2px solid #fff;
      outline-offset: 2px;
    }

    svg {
      width: 18px;
      height: 18px;
      display: block;
    }
  }

  .menuButton svg path,
  .menuButton svg circle,
  .menuButton svg line {
    stroke: #fff !important;
  }

  ${({ $open, theme }) =>
    !$open &&
    `
    ${StyledSideMenu} a.space {
      margin: 0 auto !important;
      width: 42px;
      min-height: 42px;
      padding: 0 !important;
      display: flex !important;
      align-items: center;
      justify-content: center;
      border-radius: ${theme?.radiusSm || '8px'};
    }

    ${StyledSideMenu} .nav-section {
      gap: 10px;
    }

    ${StyledSideMenu} .nav-section + .nav-section {
      margin-top: 10px;
    }

    ${StyledSideMenu} .section-divider {
      margin: 4px 16px 8px;
    }
  `}
`;

export default SideBar;
